import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  Renderer2,
  ElementRef
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DateFilterFn, MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { ICalendarAvailability } from '@core/availability/calendar-availability.model';
import { ComponentBase } from '@core/base/base.component.base';
import { IDictionary } from '@core/common.model';
import { DateFormatService } from '@core/date/date-format.service';
import { takeUntil, tap } from 'rxjs/operators';
import { VenueConfigService } from '../venue-config/venue-config.service';
import { CalendarHeaderComponent } from './calendar-header/calendar-header.component';
import { VenueCurrencyPipe } from '@core/venue-currency/venue-currency.pipe';
import { ProductBestPricingService } from '@core/product/product-best-price.service';

@Component({
  selector: 'app-calendar-availability',
  templateUrl: './calendar-availability.component.html',
  styleUrls: ['./calendar-availability.component.scss'],
  providers: [VenueCurrencyPipe]
})
export class CalendarAvailabilityComponent extends ComponentBase implements AfterViewInit, OnChanges {
  @Input() currentDate = new Date();
  @Input() availabilityData!: ICalendarAvailability[];
  @Input() isDataVisible = false;
  @Input() showBestPrice = false;

  @Output() dateSelected: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() calendarActiveDate: EventEmitter<Date> = new EventEmitter<Date>();

  availabilityHeader = CalendarHeaderComponent;
  startDate = new Date();
  customCssClasses: IDictionary<string> = {
    available: 'custom-date-class-green',
    filling: 'custom-date-class-amber',
    soldOut: 'custom-date-class-sold-out',
    onlineSalesClosed: 'custom-date-class-online-closed',
    notAvailable: ''
  };

  @ViewChild('availabilityCalendar') availabilityCalendar!: MatCalendar<Date>;
  @ViewChild('availabilityCalendar', { read: ElementRef }) calendarElementRef!: ElementRef;

  constructor(
    private dateFormatService: DateFormatService,
    private dateAdapter: DateAdapter<Date>,
    private venueConfigService: VenueConfigService,
    private renderer: Renderer2,
    private venueCurrencyPipe: VenueCurrencyPipe,
    private productBestPricingService: ProductBestPricingService
  ) {
    super();
    this.dateAdapter.setLocale(this.dateFormatService.venueLocale);
    const venueStartDate = this.venueConfigService.getVenueConfig().dateSettings.date;
    if (venueStartDate && this.dateFormatService.isDayBeforeFirstDate(venueStartDate, this.startDate)) {
      this.startDate = this.dateFormatService.dateOnlyStringToDate(venueStartDate);
    }
  }

  ngAfterViewInit(): void {
    this.availabilityCalendar.stateChanges
      .pipe(
        tap(() => this.calendarActiveDate.emit(this.availabilityCalendar.activeDate)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  ngOnChanges({ availabilityData }: SimpleChanges): void {
    if (!!availabilityData && !availabilityData.firstChange) {
      this.availabilityCalendar.updateTodaysDate();
      if (this.showBestPrice) {
        // setTimeout is used to ensure the calendar is fully rendered before we try to add prices.
        setTimeout(() => this.addPrices(availabilityData.currentValue));
      }
    }
  }

  /**
   * This is a hack to add the prices into each cell of the mat-calendar.
   * The mat-calendar component does not provide a way to add additional content into the cells.
   * Here we are finding each cell by the date class name added previously in the dateClass function
   * and then adding a data-price attribute to each cell. This attribute is used in css to add a pseudo-element with that price.
   * This can be removed once we have a calendar component in the design system that supports this.
   */
  private addPrices(availability: ICalendarAvailability[]) {
    availability
      .filter((x) => !!x.bestPrice)
      .forEach((x) => {
        const cell = this.calendarElementRef.nativeElement.querySelector(
          `.${this.getCellClassForQuerySelector(x.date)}`
        );
        const price = this.venueCurrencyPipe.transform(
          x.bestPrice,
          this.venueConfigService.getCurrencySettings(),
          '1.0-0'
        );

        if (cell && price) {
          this.renderer.setAttribute(cell.children[0], 'data-price', price);
        }
      });
  }

  /** Sets any dates that are not-available to disabled state. */
  dateFilter: DateFilterFn<Date> = (cellDate: Date | null): boolean => {
    if (!cellDate) {
      return false;
    }

    const date = this.dateFormatService.dateToStringDateOnlyHyphen(cellDate);
    const findAvailable = this.availabilityData.find(
      (dayAvailable: ICalendarAvailability) => dayAvailable.date === date
    );
    if (!findAvailable) {
      return false;
    }

    return findAvailable.availability !== 'notAvailable';
  };

  /**
   * Function triggered on rendering of the mat-calendar. Applies additional classes to the mat-calendar-body-cell.
   * @param cellDate date object to evaluate.
   * @param view current view of the calendar (only interested in month).
   * @returns css class name as string.
   */
  dateClass: MatCalendarCellClassFunction<Date> = (cellDate: Date, view: string): string => {
    const availabilityClass = this.getAvailabilityClass(cellDate, view);
    const date = this.dateFormatService.dateToStringDateOnlyHyphen(cellDate);
    const findAvailable = this.availabilityData.find(
      (dayAvailable: ICalendarAvailability) => dayAvailable.date === date
    );
    const isAvailable =
      findAvailable && findAvailable.availability !== 'notAvailable' && findAvailable.availability !== 'soldOut';

    return `${this.getCellClassForQuerySelector(cellDate)} ${availabilityClass} ${this.showBestPrice && isAvailable ? 'best-price' : ''}`;
  };

  private getAvailabilityClass(cellDate: Date, view: string): string {
    if (view === 'month' && this.availabilityData.length > 0 && this.isDataVisible) {
      const date = this.dateFormatService.dateToStringDateOnlyHyphen(cellDate);
      const findAvailable = this.availabilityData.find(
        (dayAvailable: ICalendarAvailability) => dayAvailable.date === date
      );
      const isNotDayBeforeFirstDate = !this.dateFormatService.isDayBeforeFirstDate(this.startDate, cellDate);

      if (
        findAvailable &&
        isNotDayBeforeFirstDate &&
        Object.prototype.hasOwnProperty.call(this.customCssClasses, findAvailable.availability)
      ) {
        return this.customCssClasses[findAvailable.availability];
      }
    }

    return '';
  }

  private getCellClassForQuerySelector(date: Date | string): string {
    return `date-${this.dateFormatService.dateToStringDateOnlyHyphen(new Date(date))}`;
  }

  /** Emits any date selected events. */
  onDateSelected(ev: Date | null): void {
    if (!ev) {
      return;
    }

    this.dateSelected.emit(ev);
  }
}
